// Import required components and types from Octuple UI library
import {
  Button,
  ButtonVariant,
  LabelSize,
  TextInput,
  TextInputSize,
  TextInputWidth,
  TextInputIconAlign,
  IconName,
} from '@eightfold.ai/octuple';
import React, { useState } from 'react';
import { formatRaterGroupName } from '../../commons/commonFunctions';

// GenerateRow: Renders a single row for entering rater details (First Name, Last Name, Email)
// Handles validation, API calls, and error display for each rater row
const GenerateRow = (props: any) => {
  // Updates the value of a field in the current row and propagates changes to parent state
  // This triggers a re-render of the parent section with updated data
  const changeRowData = (value: any, key: any, errorKey: any, sectionKey: any) => {
    let newFields = { ...props?.allData?.fieldsData };
    newFields[sectionKey].rows[props.index][key] = value;
    props?.allData?.fieldState(newFields);
  };

  // Determines if the required indicator (*) should be shown for the row
  // Shows if any field is filled or has an error
  const checkRequired =
    props?.data?.errors?.firstNameErrMsg ||
    props?.data?.errors?.lastNameErrMsg ||
    props?.data?.errors?.emailErrMsg ||
    props?.data?.disableRow ||
    (props?.data?.firstName && props?.data?.lastName && props?.data?.email) ||
    props?.data?.firstName ||
    props?.data?.lastName ||
    props?.data?.email;
  // Renders the row UI:
  // - Label, three input fields, error messages, and Add Rater button
  // - Button is disabled if loading, row is disabled, or already added

  const isRowRequired = Boolean(checkRequired || props?.data?.rowRequired);
  return (
    <div className='sectionRow'>
      <div className='sectionColumn columnLabel'>
        {formatRaterGroupName(props?.rowLabel)}
        {isRowRequired ? '*' : ''}

        {(props?.disableSection || props?.data?.disableRow) && (
          <div className='nominationPill'>
            <div className='nominationPillIcon'></div>
            Nominated
          </div>
        )}
      </div>
      <div className='sectionColumnWrap'>
        <div className='sectionColumn columnInput'>
          <TextInput
            htmlType='text'
            id='firstName'
            clearable={!props?.data?.disableRow}
            allowDisabledFocus={props?.data?.disableRow}
            labelProps={{
              text: `First Name${(isRowRequired) ? '*' : ''}`,
              size: LabelSize.Medium,
              classNames: 'labelText'
            }}
            onChange={(e: any) => {
              changeRowData(e.target.value, 'firstName', 'firstNameErrMsg', props?.sectionKey);
            }}
            maxlength={100}
            minlength={0}
            name='firstName'
            value={props?.data?.firstName}
            inputWidth={TextInputWidth.fill}
            classNames='inputTextField'
            size={TextInputSize.Large}
            disabled={props?.disableSection || props?.data?.disableRow}
            iconProps={props?.data?.disableRow && { path: IconName.mdiLock }}
            alignIcon={props?.data?.disableRow && TextInputIconAlign.Right}
          />
        </div>
        <div className='sectionColumn columnInput'>
          <TextInput
            htmlType='text'
            id='lastName'
            clearable={!props?.data?.disableRow}
            allowDisabledFocus={props?.data?.disableRow}
            labelProps={{
              text: `Last Name${(isRowRequired) ? '*' : ''}`,
              size: LabelSize.Medium,
              classNames: 'labelText'
            }}
            onChange={(e: any) => {
              changeRowData(e.target.value, 'lastName', 'lastNameErrMsg', props?.sectionKey);
            }}
            iconProps={props?.data?.disableRow && { path: IconName.mdiLock }}
            maxlength={100}
            minlength={0}
            name='lastName'
            value={props?.data?.lastName}
            inputWidth={TextInputWidth.fill}
            classNames='inputTextField'
            size={TextInputSize.Large}
            disabled={props?.disableSection || props?.data?.disableRow}
            alignIcon={props?.data?.disableRow && TextInputIconAlign.Right}
          />
        </div>
        <div className='sectionColumn columnInput'>
          <TextInput
            htmlType='text'
            id='email'
            clearable={!props?.data?.disableRow}
            allowDisabledFocus={props?.data?.disableRow}
            labelProps={{
              text: `Email${(isRowRequired) ? '*' : ''}`,
              size: LabelSize.Medium,
              classNames: 'labelText'
            }}
            iconProps={props?.data?.disableRow && { path: IconName.mdiLock }}
            onChange={(e: any) => {
              const el = e.target;
              const start = el.selectionStart;
              const end = el.selectionEnd;
              changeRowData(el.value.toLowerCase(), 'email', 'emailErrMsg', props?.sectionKey);
              requestAnimationFrame(() => el.setSelectionRange(start, end));
            }}
            maxlength={100}
            minlength={0}
            name='email'
            value={props?.data?.email}
            inputWidth={TextInputWidth.fill}
            classNames='inputTextField'
            size={TextInputSize.Large}
            disabled={props?.disableSection || props?.data?.disableRow}
            alignIcon={props?.data?.disableRow && TextInputIconAlign.Right}
          />
        </div>
      </div>
    </div>
  );
};

// GenerateSection: Main component to render all rater sections and their rows
// Handles adding/removing rows, disabling sections, and overall data flow
const GenerateSection = (props: any) => {
  const fieldsRecordsData = props?.fieldsData;
  // buttonStates: tracks whether the Add Row button is disabled for each section
  const [buttonStates, setButtonStates] = useState<any>({});

  Object.keys(fieldsRecordsData).forEach(sectionKey => {
    const section = fieldsRecordsData[sectionKey];
    const isAnyRowFilled = section?.rows?.some((row: any) => row?.raterProfileId);

    if (section && section?.otherDetails) {
      section.otherDetails.hasFilledRow = isAnyRowFilled;
    }

    const areAllRequiredRowsFilled = section?.rows
      ?.filter((row: any) => row.rowRequired)
      ?.every((row: any) => row?.firstName && row?.lastName && row?.email && row?.raterProfileId);
    if (section && section?.otherDetails) {
      section.otherDetails.areAllRequiredRowsFilled = areAllRequiredRowsFilled;
    }
  });

  // Adds a new empty row to a section, up to the maximum allowed by config
  // If max reached, disables the Add Row button for that section
  const addNewrow = (sectionId: string) => {
    const previousData = { ...props?.fieldsData };
    const previousRows = [...previousData?.[sectionId]?.rows];
    const otherDetails = previousData?.[sectionId]?.otherDetails;
    // Prevent adding more rows than allowed (max constraint)
    if (previousRows.length >= otherDetails?.max) {
      setButtonStates((prevState: any) => ({
        ...prevState,
        [sectionId]: true,
      }));
      return;
    }

    // Add new row with default values for all fields and flags
    previousRows.push({
      firstName: '',
      lastName: '',
      email: '',
      disableRow: false,
      disableAddRatersBtn: false,
    });

    // Update parent state with new rows (triggers re-render)
    let newFields = { ...props?.fieldsData };
    newFields[sectionId]['rows'] = [...previousRows];
    props?.fieldState(newFields);
    // If max rows reached after adding, disable Add Row button for section
    if (previousRows.length >= otherDetails?.max) {
      setButtonStates((prevState: any) => ({
        ...prevState,
        [sectionId]: true,
      }));
    }
  };

  // Returns true if the Add Row button should be disabled for a section
  // (i.e., max rows reached)
  // const checkBtnDisable = (sectionId: string) => {
  //   const previousData = { ...props?.fieldsData };
  //   const previousRows = [...previousData?.[sectionId]?.rows];
  //   const otherDetails = previousData?.[sectionId]?.otherDetails;
  //   if (previousRows.length >= otherDetails?.max) {
  //     return true;
  //   } else {
  //     return false;
  //   }
  // };

  // Returns the disabled state for the Add Row button for a section
  // Uses buttonStates if set, otherwise checks max constraint
  // const getAddNewRowButtonStates = (item: any, otherDetails: any) => {
  //   const isDisabled = buttonStates[item] ? buttonStates[item] : checkBtnDisable(otherDetails?.sectionKey);
  //   return isDisabled;
  // };

  // Renders all rater sections:
  // - Each section has a header (title, description, and checkbox)
  // - Each section body has all rows and the Add Row button
  // - Handles error display and button states
  return (
    <>
      {fieldsRecordsData &&
        Object.keys(fieldsRecordsData)?.map((item: any, index: any) => {
          const otherDetails = fieldsRecordsData?.[item]?.otherDetails;
          const formattedSectionName = formatRaterGroupName(otherDetails?.sectionKey);
          // Fixed requirement when min, max and recommended are all equal (data-driven, not group-name based)
          const isFixedRequirement = otherDetails?.min === otherDetails?.max && otherDetails?.min === otherDetails?.recom;
          const isMaxReached = otherDetails?.max <= fieldsRecordsData?.[item]?.rows?.length;
          return (
            <div className='sectionWrapper' key={item}>
              {/* Section header: shows section name, description, and 'No members' checkbox */}
              <div className='sectionHeader'>
                <div className='sectionTitleWrap'>
                  <div className='sectionTitle'>{formatRaterGroupName(item)}</div>
                </div>
                <div className='sectionDesc'>
                  {isFixedRequirement
                    ? `Add exactly ${otherDetails?.min} ${formattedSectionName} as a rater.`
                    : `Add at least ${otherDetails?.min} ${formattedSectionName} as raters. For best results, we recommend ${otherDetails?.recom}.`}
                </div>
              </div>
              {/* Section body: renders all rater rows and the Add Row button */}
              <div className='sectionBody'>
                {fieldsRecordsData?.[item]?.rows?.map((item: any, indn: any) => {
                  return (
                    <GenerateRow
                      key={`${otherDetails?.sectionKey}-row-${indn}`}
                      rowLabel={otherDetails?.sectionKey + ' ' + (indn + 1)}
                      disableSection={otherDetails?.disableSection}
                      data={item}
                      allData={props}
                      index={indn}
                      sectionKey={otherDetails?.sectionKey}
                      assignmentData={props?.assignmentData}
                    />
                  );
                })}
                <div className='sectionRow'>
                  <div className='sectionBtnWrap'>
                    {/* Show API error message for the section if any */}
                    <span className='apiErrorMessage'>{otherDetails?.errMsg}</span>
                    {/* Button to add a new rater row (disabled if max reached or section disabled) */}

                    {isFixedRequirement && isMaxReached ? (
                      <span className='sectionSuccessMsg' style={{ color: '#35c20c' }}>{`Requirement met for ${formattedSectionName}.`}</span>
                    ) : isMaxReached ? (
                      <span className='sectionErrmsg'>{`You’ve reached the maximum allowed for ${formattedSectionName} rater group.`}</span>
                    ) : (
                      <Button
                        text={`Add ${formattedSectionName}`}
                        variant={ButtonVariant.SystemUI}
                        classNames='ratersButton'
                        onClick={() => addNewrow(otherDetails?.sectionKey)}
                        transparent={true}
                        disabled={false}//getAddNewRowButtonStates(item, otherDetails) || !otherDetails?.areAllRequiredRowsFilled
                        iconProps={{ path: IconName.mdiPlus}}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
    </>
  );
};

// Export the main GenerateSection component for use in parent modules
export default GenerateSection;
